import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, ArrowRight, Package, Sparkles, BookOpen, Download, Usb, Star,
} from 'lucide-react';
import { bundlesAPI } from '@/services/api';

export default function BundlesListPage() {
  const [loading, setLoading] = useState(true);
  const [bundles, setBundles] = useState([]);
  const [category, setCategory] = useState('all');

  useEffect(() => {
    document.title = 'Webinar Bundles — GRC Educators';
    let cancelled = false;
    setLoading(true);
    bundlesAPI.list()
      .then((res) => { if (!cancelled) setBundles(res.data.bundles || []); })
      .catch(() => { if (!cancelled) setBundles([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const categories = useMemo(() => {
    const set = new Set(bundles.map((b) => b.category).filter(Boolean));
    return ['all', ...Array.from(set)];
  }, [bundles]);

  const filtered = useMemo(() => {
    let list = bundles;
    if (category !== 'all') list = list.filter((b) => b.category === category);
    return [...list].sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0));
  }, [bundles, category]);

  return (
    <div className="min-h-screen bg-transparent" data-testid="bundles-list-page">
      {/* ========== HERO ========== */}
      <section className="relative pt-36 pb-16 bg-white border-b border-zinc-200">
        <div className="container-custom relative z-10">
          <div className="max-w-4xl">
            <span className="mono-label inline-flex items-center gap-2 text-xs text-zinc-500 bg-zinc-100 border border-zinc-200 rounded-full px-3 py-1 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-gold-500" />
              Curated Learning Collections
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-5"
                data-testid="bundles-list-title">
              <span className="text-zinc-900">Webinar </span>
              <span className="text-zinc-400">bundles.</span>
            </h1>
            <p className="text-base sm:text-lg text-zinc-600 leading-relaxed max-w-3xl">
              Hand-curated collections of our highest-rated webinars, packaged by topic for
              focused, end-to-end mastery. Get more learning, pay less, and skip the guesswork.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-10 mt-10 border-t border-zinc-200 max-w-3xl">
              <Stat value={bundles.length || '—'} label="Bundles Available" />
              <Stat value={bundles.reduce((sum, b) => sum + (b.webinar_count || 0), 0) || '—'} label="Total Webinars" />
              <Stat value="2" label="Delivery Formats" />
              <Stat value="Lifetime" label="Access" />
            </div>
          </div>
        </div>
      </section>

      {/* ========== FILTERS + GRID ========== */}
      <section className="py-16 bg-transparent">
        <div className="container-custom">
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.length > 1 && categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  category === c
                    ? 'bg-zinc-900 text-white'
                    : 'bg-white text-zinc-600 border border-zinc-200 hover:border-zinc-400'
                }`}
                data-testid={`category-filter-${c}`}
              >
                {c === 'all' ? 'All Categories' : c}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-navy-600" />
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((b) => (
                <BundleCard key={b.id} bundle={b} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

// ============== sub-components ==============

const Stat = ({ value, label }) => (
  <div>
    <div className="font-heading text-3xl sm:text-4xl font-bold text-zinc-900">{value}</div>
    <div className="mono-label text-[10px] text-zinc-500 mt-1">{label}</div>
  </div>
);

const EmptyState = () => (
  <div className="text-center py-20">
    <Package className="w-14 h-14 text-slate-300 mx-auto mb-4" />
    <p className="text-slate-500 font-medium">No bundles in this category yet</p>
    <p className="text-slate-400 text-sm mt-1">Try selecting a different category.</p>
  </div>
);

const BundleCard = ({ bundle }) => {
  const downloadSavings = bundle.original_price_download && bundle.price_download
    ? Math.round((1 - bundle.price_download / bundle.original_price_download) * 100)
    : 0;
  return (
    <Link
      to={`/bundles/${bundle.slug}`}
      className="block group"
      data-testid={`bundle-card-${bundle.slug}`}
    >
      <Card className="h-full bg-white border-zinc-200 group-hover:border-zinc-900 transition-colors rounded-xl overflow-hidden flex flex-col">
        {/* Banner */}
        <div className="relative h-40 bg-zinc-100 border-b border-zinc-200 flex items-center justify-center overflow-hidden">
          <Package className="w-14 h-14 text-navy-600 relative z-10" />
          {bundle.is_featured && (
            <Badge className="absolute top-3 right-3 bg-gold-500 text-navy-900 border-none">
              <Star className="w-3 h-3 mr-1 fill-current" /> Featured
            </Badge>
          )}
          {downloadSavings > 0 && (
            <Badge className="absolute top-3 left-3 bg-white text-green-700 border border-green-200">
              Save {downloadSavings}%
            </Badge>
          )}
        </div>

        <CardContent className="p-6 flex flex-col flex-1">
          {bundle.category && (
            <div className="mono-label text-[10px] text-navy-600 mb-2">
              {bundle.category}
            </div>
          )}
          <h3 className="font-heading text-lg font-bold text-zinc-900 leading-snug mb-2 group-hover:text-navy-700">
            {bundle.title}
          </h3>
          {bundle.subtitle && (
            <p className="text-sm text-zinc-600 line-clamp-3 mb-4 flex-1">
              {bundle.subtitle}
            </p>
          )}

          <div className="flex items-center gap-3 text-xs text-zinc-500 mb-4">
            <span className="inline-flex items-center gap-1">
              <BookOpen className="w-3.5 h-3.5" /> {bundle.webinar_count || 0} webinars
            </span>
            <span className="text-zinc-300">·</span>
            <span className="inline-flex items-center gap-1">
              <Download className="w-3.5 h-3.5" /> DL
            </span>
            <span className="inline-flex items-center gap-1">
              <Usb className="w-3.5 h-3.5" /> USB
            </span>
          </div>

          <div className="flex items-end justify-between pt-4 border-t border-zinc-100">
            <div>
              <p className="text-xs text-zinc-400 mb-0.5">Starting at</p>
              <p className="font-heading text-2xl font-bold text-zinc-900">
                ${bundle.price_download}
                <span className="text-sm font-normal text-zinc-500"> · DL</span>
              </p>
            </div>
            <span className="text-sm font-semibold text-navy-700 group-hover:text-gold-600 transition-colors inline-flex items-center gap-1">
              View
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};
